/**
 * @fileoverview Memorização de credenciais por provider/lista.
 *
 * Em TV (premissa principal do app), digitação por remoto é dolorosa
 * — letra a letra via teclado virtual. Lembrar credenciais quando user
 * volta ao mesmo provider economiza minutos a cada relogin.
 *
 * **Trade-off de segurança aceito (decisão Fernando, 2026-05-08)**:
 * salvar usuário E senha em texto puro no localStorage. TVs Tizen/webOS/
 * Android TV têm localStorage isolado por origem (mesma garantia que
 * cookies http-only não dão pra IPTV — Xtream API não usa cookies).
 * Risco real: alguém com acesso físico ao DevTools da TV vê a senha.
 * Em prática TVs não são compartilhadas e DevTools não está exposto.
 *
 * **Quando lembrar**: só após login bem-sucedido (`save()` chamado pelo
 * LoginScreen/DirectLoginScreen após `setAuth()`).
 *
 * **Quando esquecer**:
 * - Logout explícito do user (`logout('user')`) → limpa.
 * - `invalid_credentials` no submit (senha mudou no painel do parceiro)
 *   → limpa.
 * - `session_expired` (token expirou, refresh falhou) → MANTÉM —
 *   credenciais ainda válidas, só o JWT acabou.
 * - `account_expired` (plano vencido) → MANTÉM — user renova plano e
 *   tenta de novo, credenciais são as mesmas.
 */

const KEY_PREFIX = 'iptv:login-memory:'

export interface RememberedCredentials {
  username: string
  password: string
}

/** Chave de storage por contexto. Provider mode = código do parceiro;
 *  direct mode = nome da lista (URL é parte do contexto mas pode mudar
 *  entre IPs do mesmo painel — listName é estável). */
function makeKey(scopeKey: string): string {
  return `${KEY_PREFIX}${scopeKey}`
}

/** Lê credenciais lembradas pra um provider/lista. Retorna `null` se
 *  não tem nada salvo OU se o JSON gravado está corrompido (defensivo). */
export function getRemembered(scopeKey: string): RememberedCredentials | null {
  try {
    const raw = localStorage.getItem(makeKey(scopeKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'username' in parsed &&
      'password' in parsed &&
      typeof (parsed as RememberedCredentials).username === 'string' &&
      typeof (parsed as RememberedCredentials).password === 'string'
    ) {
      return parsed as RememberedCredentials
    }
    return null
  } catch {
    return null
  }
}

/** Salva credenciais. Chamar SOMENTE após login bem-sucedido. */
export function saveRemembered(
  scopeKey: string,
  credentials: RememberedCredentials,
): void {
  try {
    localStorage.setItem(makeKey(scopeKey), JSON.stringify(credentials))
  } catch {
    // localStorage cheio ou indisponível — silencioso. UX continua
    // funcional sem memorização.
  }
}

/** Esquece credenciais de um scope (logout explícito ou invalid_credentials). */
export function forgetRemembered(scopeKey: string): void {
  try {
    localStorage.removeItem(makeKey(scopeKey))
  } catch {
    // idem
  }
}
